/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import usePrevious from '@ibm-watson/shared/modules/ia/hooks/usePrevious';
import { fetchUserStart } from '@ibm-watson/shared/modules/ia/store/session/actions';
import * as userSelectors from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { doLogin } from '@ibm-watson/shared/modules/ia/util/history';
import localStorage from '@ibm-watson/shared/modules/ia/util/localStorage';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';

/**
 * This component wraps the main routes of the application.
 *
 * It kicks off the authentication process if the user is not yet
 * authenticated and when the user has logged in, it sets up the local storage
 * context so that any values saved to local storage are associated with
 * the logged-in user.
 */

const isLoggingOut = (
  oldIsAuthenticated: boolean,
  isAuthenticated: boolean
): boolean => oldIsAuthenticated && !isAuthenticated;

const isLoggedIn = (
  oldIsAuthenticated: boolean,
  isAuthenticated: boolean
): boolean => !oldIsAuthenticated && isAuthenticated;

const MainRoutesWrapper: React.FC = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { isAuthenticated, username, isChecking } = useAppSelector(state => {
    return {
      isAuthenticated: userSelectors.isAuthenticated(state),
      isChecking: userSelectors.isChecking(state),
      username: userSelectors.getUser(state)?.username
    };
  }, shallowEqual);

  const prevIsAuthenticated = usePrevious(isAuthenticated);

  useEffect(() => {
    dispatch(fetchUserStart());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn(prevIsAuthenticated, isAuthenticated)) {
      localStorage.setContext(username);
    } else if (isLoggingOut(prevIsAuthenticated, isAuthenticated)) {
      // This code is probably never reached, because any code path that would
      // cause the user to no longer be authenticated would simply trigger
      // the logout process. However, if we ever DO reach this code, cause
      // the login process to be initiated.
      doLogin();
    }
  }, [location, isAuthenticated, prevIsAuthenticated, isChecking, username]);

  return <>{children}</>;
};

export default MainRoutesWrapper;
